import React from 'react';
import { useState } from 'react';
import ToggleSettingRow from '../ToggleSettingRow'; // Adjust path
import InputSettingRow from '../InputSettingRow'; // Adjust path
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label'; // Used for Danger Zone label
import { Switch } from '../../components/ui/switch';

interface PrivacySettingsProps {
  initialSettings: {
    findMeByPhone: boolean;
    findMeByEmail: boolean;
    autoLogoutMinutes: number | string;
    autoLogoutOnClose: boolean;
    requirePasskey: boolean;
    deleteAccount: boolean;
  } | null;
  onSave: (settings: {
    findMeByPhone: boolean;
    findMeByEmail: boolean;
    autoLogoutMinutes: number | string;
    autoLogoutOnClose: boolean;
    requirePasskey: boolean;
    deleteAccount: boolean;
  }) => void;
  onCancel: () => void;
}

export default function PrivacySettings({ initialSettings, onSave, onCancel }: PrivacySettingsProps) {
  // Manage local state for toggles and input
  const [settings, setSettings] = useState(initialSettings || {
    findMeByPhone: false, // Example initial states
    findMeByEmail: true,
    autoLogoutMinutes: 120,
    autoLogoutOnClose: true,
    requirePasskey: false,
    deleteAccount: false, // Danger Zone toggle
  });

   const handleToggleChange = (id: string, checked: boolean) => {
    setSettings(prev => ({ ...prev, [id]: checked }));
  };

const handleInputChange = (id: string, value: string) => {
     // Basic validation for minutes input
     const numValue = Number.parseInt(value, 10);
     if (!Number.isNaN(numValue)) {
         setSettings(prev => ({ ...prev, [id]: numValue }));
     } else {
        setSettings(prev => ({ ...prev, [id]: value }));
     }
  };

  // Supabase Integration Point: Call onSave with the settings data
   const handleSave = () => {
      console.log("Saving privacy settings:", settings);
      // Ensure numeric value is saved if input was empty
      const settingsToSave = {
          ...settings,
          autoLogoutMinutes: settings.autoLogoutMinutes === '' ? 0 : settings.autoLogoutMinutes
      };
      if (onSave) onSave(settingsToSave);
      // Typically navigate back or show confirmation after save
   };

   // Supabase Integration Point: Discard changes and revert
    const handleCancel = () => {
       console.log("Canceling changes");
       // Revert to initial state or navigate back without saving
        setSettings(initialSettings || { // Reset to original or default
           findMeByPhone: false,
           findMeByEmail: true,
           autoLogoutMinutes: 120,
           autoLogoutOnClose: true,
           requirePasskey: false,
           deleteAccount: false,
        });
        if (onCancel) onCancel();
        // Typically navigate back
    };


  return (
    <div className="jr-card jr-screen-pad mt-4 flex flex-col overflow-hidden px-0">
      <ToggleSettingRow
        label="Allow people to find me by Phone"
        id="findMeByPhone"
        checked={settings.findMeByPhone}
        onCheckedChange={(c) => handleToggleChange('findMeByPhone', c)}
      />
      <ToggleSettingRow
        label="Allow people to find me by Email"
        id="findMeByEmail"
        checked={settings.findMeByEmail}
        onCheckedChange={(c) => handleToggleChange('findMeByEmail', c)}
      />
      <InputSettingRow
        label="Auto Logout after X minutes"
        id="autoLogoutMinutes"
        type="number"
        value={settings.autoLogoutMinutes}
        onChange={(e) => handleInputChange('autoLogoutMinutes', e.target.value)}
      />
       <ToggleSettingRow
        label="Auto Logout When Window is Closed"
        id="autoLogoutOnClose"
        checked={settings.autoLogoutOnClose}
        onCheckedChange={(c) => handleToggleChange('autoLogoutOnClose', c)}
      />
       <ToggleSettingRow
        label="Require Passkey to Unlock"
        id="requirePasskey"
        checked={settings.requirePasskey}
        onCheckedChange={(c) => handleToggleChange('requirePasskey', c)}
      />

      {/* Save/Cancel Buttons */}
      <div className="flex space-x-4 border-b border-[color:var(--jr-border)] p-4">
           <Button onClick={handleSave} className="jr-action-primary h-auto flex-1 min-h-11">Save</Button>
           <Button onClick={handleCancel} variant="outline" className="jr-action-secondary h-auto flex-1 min-h-11">Cancel</Button>
       </div>

       {/* Danger Zone */}
       <div className="p-4">
           <h3 className="text-sm font-bold text-[color:#efc2bd]">DANGER ZONE:</h3>
            <div className="flex items-center justify-between py-4">
                <Label htmlFor="deleteAccount" className="flex-grow cursor-pointer text-base text-[color:#efc2bd]">Delete Account Permanently</Label>
                <Switch id="deleteAccount" checked={settings.deleteAccount} onCheckedChange={(c) => handleToggleChange('deleteAccount', c)} className="data-[state=checked]:bg-[color:var(--jr-danger)] data-[state=unchecked]:bg-[color:var(--jr-surface-muted)]" /> {/* Style red when checked */}
            </div>
            {settings.deleteAccount && ( // Show warning only when toggle is on
                 <p className="mt-1 text-sm italic text-[color:#efc2bd]">WARNING! This action cannot be undone.</p>
            )}
       </div>
    </div>
  );
}
